import { Outlet } from "@tanstack/react-router";
import { type ReactNode, useSyncExternalStore } from "react";
import { useWorkspaceContext } from "@/contexts/workspace-context";
import { DesktopOnly } from "./desktop-only";
import { Header } from "./header";
import { Sidebar } from "./sidebar";

// 1024px = Tailwind's `lg` breakpoint (the plan's Global Constraints:
// "supported viewports >=1024px; below that render a 'desktop only'
// screen"). Tracked via a `resize` listener + `window.innerWidth` rather
// than `matchMedia` — simpler to drive from tests (set `innerWidth`, fire a
// `resize` event) and happy-dom implements both reliably.
const NARROW_BREAKPOINT_PX = 1024;

function isNarrow(): boolean {
    return typeof window !== "undefined" && window.innerWidth < NARROW_BREAKPOINT_PX;
}

function useIsNarrowViewport(): boolean {
    return useSyncExternalStore(
        (onChange) => {
            if (typeof window === "undefined") return () => {};
            window.addEventListener("resize", onChange);
            return () => window.removeEventListener("resize", onChange);
        },
        isNarrow,
        () => false,
    );
}

interface AppShellProps {
    children?: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
    const narrow = useIsNarrowViewport();
    const { workspaceName } = useWorkspaceContext();

    if (narrow) {
        return <DesktopOnly workspaceName={workspaceName} />;
    }

    return (
        // overflow-clip, not -hidden: a hidden box still scrolls when code asks
        // it to (scrollIntoView walks every ancestor), which slid the header
        // off-screen with nothing to scroll it back. clip can't scroll at all.
        <div className="flex h-screen flex-col overflow-clip bg-background">
            <Header />
            {/* min-h-0: unlike overflow-hidden, clip doesn't drop a flex
                item's minimum height to zero, and without it this row grows
                to the page's full height and <main> never scrolls. */}
            <div className="flex min-h-0 flex-1 overflow-clip">
                <Sidebar />
                <main
                    id="main-content"
                    tabIndex={-1}
                    // Still scrollable: the detail pages and forms are taller
                    // than the viewport and should scroll. The list pages pin
                    // themselves to it with h-full instead, so they never
                    // reach for this scrollbar.
                    //
                    // `scrollbar-gutter: stable` reserves the gutter even when
                    // the content fits, so navigating from a list (fits, no
                    // scrollbar) to a detail page (scrolls) doesn't shift the
                    // content sideways by the scrollbar's width. Ported from
                    // the vouchers admin, which documents the same jump.
                    //
                    // `relative` makes this the containing block for anything
                    // absolutely positioned inside a page. Base UI's Select
                    // renders a visually hidden native input that is
                    // position:absolute; with no positioned ancestor it
                    // anchored to the document, landed below the fold and made
                    // the whole document scrollable.
                    className="relative flex-1 overflow-y-auto p-6 [scrollbar-gutter:stable]"
                >
                    {children ?? <Outlet />}
                </main>
            </div>
        </div>
    );
}
